import {
  getSecurityMarketDataCellValue,
  InlineFormattedNumber,
  type Column,
  type MarketDataSnapshotLevel,
  type SecurityMarketDataCellValue,
} from '@talos/kyoko';
import type { Column as AgColumn, ColDef, ICellRendererParams, IRowNode, ValueGetterParams } from 'ag-grid-community';
import { get } from 'lodash-es';
import { getPriceLadderDepth } from '../expandSecurityToPriceGridRows';
import { isPriceLadderMode, type PriceGridLadderType } from '../ladder';
import type { PriceGridEntity } from '../PriceGridEntity';
import { getPriceLadderLevelAtStep } from '../priceLadderLevels';
import { PRICE_GRID_MARKET_DATA_COLLECTION_PATH, PRICE_GRID_MARKET_DATA_COLUMN_TYPE } from './constants';
import { getPriceGridMarketDataRequest } from './marketData';
import {
  getPriceGridMarketDataColumnId,
  PRICE_GRID_MARKET_DATA_BID_QTY_COLUMN_SUFFIX,
  PRICE_GRID_MARKET_DATA_LEAF_COLUMN_PROPS,
  PRICE_GRID_MARKET_DATA_OFFER_QTY_COLUMN_SUFFIX,
  type PriceGridMarketDataColumnRow,
} from './priceGridMarketDataColumnUtils';
import type { PriceGridSecurityMarketDataColumnParams } from './types';

export interface PriceGridLadderColumnConfig {
  ladderType: PriceGridLadderType;
  priceIncrement: string;
}

const EMPTY_PRICE_LADDER_LEVEL = {} as MarketDataSnapshotLevel;

function getPriceGridMarketDataColumnStub(
  columnParams: PriceGridSecurityMarketDataColumnParams,
  field: string
): AgColumn<PriceGridEntity> {
  return {
    getColDef: () => ({
      field,
      cellRendererParams: {
        priceGridMarketDataColumnType: PRICE_GRID_MARKET_DATA_COLUMN_TYPE,
        priceGridMarketDataColumnParams: columnParams,
      },
    }),
  } as AgColumn<PriceGridEntity>;
}

export function getPriceGridPriceLadderCellValue({
  row,
  columnParams,
  field,
}: {
  row: PriceGridEntity;
  columnParams: PriceGridSecurityMarketDataColumnParams;
  field: string;
}): SecurityMarketDataCellValue {
  const request = getPriceGridMarketDataRequest(getPriceGridMarketDataColumnStub(columnParams, field), row);
  if (!request) {
    return undefined;
  }

  const snapshot = get(row, [PRICE_GRID_MARKET_DATA_COLLECTION_PATH, request.key]);
  const level = getPriceLadderLevelAtStep(snapshot, {
    priceStepIndex: row.bucket.priceStepIndex ?? 0,
    depth: getPriceLadderDepth(row.sourceItem),
  });

  return snapshot
    ? {
        marketData: snapshot,
        bid: level?.bid ?? EMPTY_PRICE_LADDER_LEVEL,
        offer: level?.offer ?? EMPTY_PRICE_LADDER_LEVEL,
      }
    : undefined;
}

export function getPriceGridMarketDataQtyCellValue({
  row,
  columnParams,
  field,
  ladderConfig,
  side,
}: {
  row: PriceGridEntity | undefined;
  columnParams: PriceGridSecurityMarketDataColumnParams;
  field: string;
  ladderConfig: PriceGridLadderColumnConfig;
  side: 'bid' | 'offer';
}): string | undefined {
  if (!row || !isPriceLadderMode(ladderConfig.ladderType)) {
    return undefined;
  }

  const value = getPriceGridPriceLadderCellValue({
    row,
    columnParams,
    field,
  });
  const size = side === 'bid' ? value?.bid?.Size : value?.offer?.Size;

  if (!size || size === '0') {
    return row.isParentRow ? '' : undefined;
  }

  return size;
}

function QtyCellRenderer({ value, security }: { value: string | undefined; security: PriceGridEntity['security'] }) {
  if (!value) {
    return null;
  }

  return <InlineFormattedNumber number={value} specification={security.MinSizeIncrement} align="right" round />;
}

type PriceGridMarketDataQtyColumnConfig = {
  side: 'bid' | 'offer';
  suffix: string;
  title: string;
};

function getPriceGridMarketDataQtyColumn(
  column: Column<PriceGridSecurityMarketDataColumnParams, PriceGridMarketDataColumnRow>,
  ladderConfig: PriceGridLadderColumnConfig,
  { side, suffix, title }: PriceGridMarketDataQtyColumnConfig
): Column<ColDef<PriceGridEntity, string>, PriceGridMarketDataColumnRow> {
  const colId = getPriceGridMarketDataColumnId(column, suffix);
  const columnParams = column.params;
  const field = column.field ?? 'security.Symbol';
  const getQtyCellValue = (row: PriceGridEntity | undefined) =>
    columnParams
      ? getPriceGridMarketDataQtyCellValue({
          row,
          columnParams,
          field,
          ladderConfig,
          side,
        })
      : undefined;

  return {
    type: 'custom',
    id: colId,
    field: column.field,
    title,
    initialWidth: 80,
    dynamics: column.dynamics,
    sortable: false,
    ...PRICE_GRID_MARKET_DATA_LEAF_COLUMN_PROPS,
    params: {
      colId,
      headerName: title,
      cellRendererParams: {
        priceGridMarketDataColumnType: PRICE_GRID_MARKET_DATA_COLUMN_TYPE,
        priceGridMarketDataColumnParams: column.params,
      },
      valueGetter: (params: ValueGetterParams<PriceGridEntity, string>) => getQtyCellValue(params.data),
      cellRenderer: (params: ICellRendererParams<PriceGridEntity, string>) => {
        const row = params.data;
        if (!row || !columnParams) {
          return null;
        }
        const value = params.value ?? getQtyCellValue(row);
        return <QtyCellRenderer value={value} security={row.security} />;
      },
    },
  };
}

export function getPriceGridMarketDataBidQtyColumn(
  column: Column<PriceGridSecurityMarketDataColumnParams, PriceGridMarketDataColumnRow>,
  ladderConfig: PriceGridLadderColumnConfig
): Column<ColDef<PriceGridEntity, string>, PriceGridMarketDataColumnRow> {
  return getPriceGridMarketDataQtyColumn(column, ladderConfig, {
    side: 'bid',
    suffix: PRICE_GRID_MARKET_DATA_BID_QTY_COLUMN_SUFFIX,
    title: 'Bid Qty',
  });
}

export function getPriceGridMarketDataOfferQtyColumn(
  column: Column<PriceGridSecurityMarketDataColumnParams, PriceGridMarketDataColumnRow>,
  ladderConfig: PriceGridLadderColumnConfig
): Column<ColDef<PriceGridEntity, string>, PriceGridMarketDataColumnRow> {
  return getPriceGridMarketDataQtyColumn(column, ladderConfig, {
    side: 'offer',
    suffix: PRICE_GRID_MARKET_DATA_OFFER_QTY_COLUMN_SUFFIX,
    title: 'Offer Qty',
  });
}

export function getPriceGridMarketDataCellValueWithLadder({
  columnParams,
  params,
  ladderConfig,
}: {
  columnParams: PriceGridSecurityMarketDataColumnParams;
  params: Pick<ValueGetterParams<PriceGridEntity, SecurityMarketDataCellValue>, 'column' | 'data'>;
  ladderConfig: PriceGridLadderColumnConfig;
}): SecurityMarketDataCellValue {
  const row = params.data;
  if (!row || !params.column) {
    return undefined;
  }

  if (isPriceLadderMode(ladderConfig.ladderType)) {
    const field = params.column.getColDef().field ?? 'security.Symbol';
    return getPriceGridPriceLadderCellValue({
      row,
      columnParams,
      field,
    });
  }

  const request = getPriceGridMarketDataRequest(params.column, row);
  if (!request) {
    return undefined;
  }

  return getSecurityMarketDataCellValue(
    {
      field: params.column.getColDef().field,
      params: {
        ...columnParams,
        Markets: request.Markets,
      },
    } as Column<PriceGridSecurityMarketDataColumnParams, PriceGridMarketDataColumnRow>,
    { data: row } as IRowNode<PriceGridEntity>
  );
}
